import { Link } from "react-router-dom";
import PageBanner from "../../PageBanner";
import SocialMediaSidebar from "../../SocialMediaSidebar";
import SectionHeader from "../../SectionHeader";
import ScrollToTopButton from "../../ScrollToTopButton";
import SectionHeaderNoPDF from "../../SectionHeaderNoPDF";

const OfferingRouteOptimization = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Route Optimization"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Offerings" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="Route Optimization @ AJEEVI"
          shareUrl="https://swmecom.ajeevi.in/route-optimization"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Page content */}
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800 text-justify">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Summary Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Summary
            </h4>
            <p className="text-black mb-4 text-lg">
              AI-powered Route Optimization transforms waste collection operations by calculating the most efficient paths for garbage trucks, reducing fuel consumption, and improving service reliability. Our advanced algorithms consider multiple dynamic factors to create optimal daily routes.
            </p>
            <p className="text-black text-lg">
              Municipalities and waste management companies using our route optimization achieve significant cost savings, reduced emissions, and improved workforce productivity while maintaining or enhancing service quality for residents and businesses.
            </p>
          </div>

          {/* Introduction Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              Introduction
            </h4>
            <p className="text-black mb-4 text-lg">
              Traditional waste collection routes often follow historical patterns that no longer reflect current realities. AJEEVI's Route Optimization solution uses artificial intelligence to continuously analyze and adapt collection routes based on real-world conditions and operational constraints.
            </p>
            <p className="text-black text-lg">
              By processing vast amounts of data including traffic patterns, bin fill levels, vehicle capacities, and driver availability, our system generates routes that maximize efficiency while accommodating the dynamic nature of urban environments.
            </p>
          </div>

          {/* Key Features Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-4 text-black">
              Key Features of Route Optimization System:
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>
                <strong>Dynamic Routing Engine:</strong> AI that recalculates optimal paths based on changing conditions
              </li>
              <li>
                <strong>Multi-Constraint Optimization:</strong> Balances vehicle capacity, time windows, driver shifts, and priority locations
              </li>
              <li>
                <strong>Real-Time Adjustments:</strong> Adapts to unexpected road closures, traffic, or service requests
              </li>
              <li>
                <strong>Predictive Modeling:</strong> Anticipates seasonal variations and special event impacts
              </li>
              <li>
                <strong>Zone Balancing:</strong> Evenly distributes workload across vehicles and crews
              </li>
              <li>
                <strong>Integration with Smart Bins:</strong> Prioritizes collections based on actual fill levels
              </li>
            </ul>
          </div>

          {/* Benefits Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Operational Benefits
            </h5>
            <p className="text-black text-lg mb-4">
              Implementing AI-powered Route Optimization delivers measurable improvements:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>20-35% reduction in fuel costs through minimized driving distances</li>
              <li>15-30% more collections completed per shift</li>
              <li>Reduced vehicle wear and maintenance costs</li>
              <li>25-45% lower carbon emissions from optimized routes</li>
              <li>Improved driver satisfaction with balanced workloads</li>
              <li>Enhanced service reliability with predictable completion times</li>
            </ul>
          </div>

          {/* Implementation Section */}
          <div className="mb-10">
            <h5 className="text-xl font-semibold mb-3 text-black">
              Implementation Considerations
            </h5>
            <p className="text-black text-lg mb-4">
              Successful route optimization deployment requires:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Accurate baseline data on current routes and performance</li>
              <li>Vehicle tracking hardware for real-time position monitoring</li>
              <li>Clear operational constraints and service level agreements</li>
              <li>Staff training on new routing procedures and tools</li>
              <li>Pilot testing with select routes before full deployment</li>
              <li>Ongoing performance monitoring and system tuning</li>
            </ul>
          </div>

          {/* Ajeevi's Contribution Section */}
          <div>
            <h5 className="text-xl font-semibold mb-3 text-black">
              AJEEVI's Route Optimization Solutions
            </h5>
            <p className="text-black mb-4 text-lg">
              AJEEVI offers specialized route optimization for waste management:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>
                <strong>Smart Routing Platform:</strong> End-to-end route planning and execution system
              </li>
              <li>
                <strong>Mobile Workforce App:</strong> Turn-by-turn navigation with real-time updates for drivers
              </li>
              <li>
                <strong>Fleet Management Integration:</strong> Coordination with vehicle telematics and maintenance systems
              </li>
              <li>
                <strong>Scenario Planning:</strong> Compare alternative routing strategies before implementation
              </li>
              <li>
                <strong>Performance Analytics:</strong> Detailed reporting on route efficiency improvements
              </li>
            </ul>
            <p className="text-black text-lg">
              Discover how AJEEVI's Route Optimization can transform your collection operations. Visit{" "}
              <a
                href="http://www.ajeevi.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00D084] hover:underline"
              >
                www.ajeevi.com
              </a>{" "}
              for more information.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferingRouteOptimization;